import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import {
  OFFSHORE_EXCEL_HEADERS,
  buildOffshoreDisplayName,
  isWelderJobDescription,
  normalizeRosterShift,
  parseOnboardValue,
} from './dprOffshorePersonnel';

const HEADER_ALIASES = {
  rosterId: ['id', '#'],
  jobDescription: ['job description', 'job desc', 'jobdescription', 'role', 'designation'],
  lastName: ['last name', 'lastname', 'surname'],
  firstName: ['first name', 'firstname', 'given name'],
  nationality: ['nationality', 'nation'],
  onboard: ['onboard', 'on board', 'on-board'],
  shift: ['shift'],
  station: ['station', 'stn', 'station #', 'station number'],
};

const normalizeHeaderCell = (cell) =>
  String(cell ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const findHeaderColumnIndex = (headers, aliases) => {
  for (let i = 0; i < headers.length; i += 1) {
    const h = normalizeHeaderCell(headers[i]);
    if (aliases.some((alias) => h === alias || h.includes(alias))) {
      return i;
    }
  }
  return -1;
};

const findHeaderRowIndex = (rows) => {
  for (let r = 0; r < Math.min(rows.length, 20); r += 1) {
    const row = rows[r];
    if (!Array.isArray(row)) continue;
    const idIdx = findHeaderColumnIndex(row, HEADER_ALIASES.rosterId);
    const jobIdx = findHeaderColumnIndex(row, HEADER_ALIASES.jobDescription);
    const lastIdx = findHeaderColumnIndex(row, HEADER_ALIASES.lastName);
    if (idIdx >= 0 && jobIdx >= 0 && lastIdx >= 0) {
      return r;
    }
  }
  return 0;
};

const buildColumnMap = (headerRow) => {
  const map = {};
  Object.entries(HEADER_ALIASES).forEach(([field, aliases]) => {
    map[field] = findHeaderColumnIndex(headerRow, aliases);
  });
  return map;
};

const cellAt = (row, index) => {
  if (index < 0 || index >= row.length) return '';
  const val = row[index];
  if (val === undefined || val === null) return '';
  return String(val).trim();
};

/**
 * Parse offshore personnel roster rows from an Excel sheet (8-column template or full Golfinho sheet).
 * Returns parsed row objects ready for preview / table replace.
 */
export const parseOffshorePersonnelExcelRows = (rows) => {
  if (!rows || rows.length < 2) {
    throw new Error('The uploaded file has no data rows.');
  }

  const headerRowIndex = findHeaderRowIndex(rows);
  const headerRow = rows[headerRowIndex];
  const col = buildColumnMap(headerRow);

  if (col.jobDescription < 0 || col.lastName < 0 || col.firstName < 0) {
    throw new Error(
      'Could not find required columns (ID, Job Description, Last Name, First Name). Check the template headers.'
    );
  }

  const parsed = [];
  for (let r = headerRowIndex + 1; r < rows.length; r += 1) {
    const row = rows[r];
    if (!Array.isArray(row)) continue;

    const jobDescription = cellAt(row, col.jobDescription);
    const lastName = cellAt(row, col.lastName);
    const firstName = cellAt(row, col.firstName);
    const station = cellAt(row, col.station);
    const rosterIdRaw = cellAt(row, col.rosterId);

    const hasContent = jobDescription || lastName || firstName || station || rosterIdRaw;
    if (!hasContent) continue;

    const rosterId = rosterIdRaw ? rosterIdRaw : parsed.length + 1;
    const onboard = parseOnboardValue(cellAt(row, col.onboard));
    const rosterShift = normalizeRosterShift(cellAt(row, col.shift));
    const nationality = cellAt(row, col.nationality);
    const name = buildOffshoreDisplayName(firstName, lastName);

    parsed.push({
      rosterId,
      jobDescription,
      lastName,
      firstName,
      nationality,
      onboard,
      rosterShift,
      allocatedStation: station,
      name,
      planned: 0,
      actual: 0,
      welderId: isWelderJobDescription(jobDescription) ? null : null,
      isNew: true,
    });
  }

  if (parsed.length === 0) {
    throw new Error('No personnel rows found in the uploaded file.');
  }

  return parsed;
};

export const readExcelRowsFromFile = async (file) => {
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  return XLSX.utils.sheet_to_json(worksheet, { header: 1 });
};

export const downloadOffshorePersonnelTemplate = async () => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Personnel Roster');

  worksheet.addRow(OFFSHORE_EXCEL_HEADERS);
  worksheet.getRow(1).font = { bold: true };
  worksheet.columns = OFFSHORE_EXCEL_HEADERS.map((title) => ({
    header: title,
    width: Math.max(14, String(title).length + 4),
  }));

  worksheet.addRow(['1', 'Senior Technician', 'Smith', 'John', 'British', 'Yes', 'Day', '']);
  worksheet.addRow(['2', 'Welder UK', 'Jones', 'Jane', 'British', 'Yes', 'Night', 'st 1']);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'offshore_personnel_roster_template.xlsx';
  link.click();
  URL.revokeObjectURL(url);
};
